import { useState, useEffect } from 'react';

interface PWAState {
  isInstalled: boolean;
  isInstallable: boolean;
  isOnline: boolean;
  isUpdateAvailable: boolean;
}

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

export const usePWA = () => {
  const [state, setState] = useState<PWAState>({
    isInstalled: false,
    isInstallable: false,
    isOnline: navigator.onLine,
    isUpdateAvailable: false,
  });

  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    // Vérifier si l'app est installée
    const checkInstallStatus = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      const isInWebAppiOS = (window.navigator as any).standalone === true;
      const isInstalled = isStandalone || isInWebAppiOS;

      setState((prev) => ({ ...prev, isInstalled }));
    };

    // Gérer l'événement beforeinstallprompt
    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setState((prev) => ({ ...prev, isInstallable: true }));
    };

    // Gérer l'installation
    const handleAppInstalled = () => {
      setState((prev) => ({
        ...prev,
        isInstalled: true,
        isInstallable: false,
      }));
      setDeferredPrompt(null);
    };

    // Gérer le statut en ligne/hors ligne
    const handleOnline = () => setState((prev) => ({ ...prev, isOnline: true }));
    const handleOffline = () => setState((prev) => ({ ...prev, isOnline: false }));

    // Gérer les mises à jour du service worker
    const handleServiceWorkerUpdate = () => {
      setState((prev) => ({ ...prev, isUpdateAvailable: true }));
    };

    // Ajouter les event listeners
    checkInstallStatus();
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Service Worker update detection
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', handleServiceWorkerUpdate);
    }

    // Cleanup
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);

      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('controllerchange', handleServiceWorkerUpdate);
      }
    };
  }, []);

  // Fonction pour installer l'app
  const installApp = async (): Promise<boolean> => {
    if (!deferredPrompt) return false;

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        setState((prev) => ({
          ...prev,
          isInstallable: false,
        }));
        setDeferredPrompt(null);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Erreur lors de l'installation:", error);
      return false;
    }
  };

  // Fonction pour mettre à jour l'app
  const updateApp = () => {
    setState((prev) => ({ ...prev, isUpdateAvailable: false }));
    window.location.reload();
  };

  // Fonction pour partager l'app
  const shareApp = async (data?: ShareData) => {
    const defaultData: ShareData = {
      title: 'Datasset',
      text: 'Discover Datasset - Your Portfolio Management Application',
      url: window.location.origin,
    };

    const shareData = { ...defaultData, ...data };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return true;
      } catch (error) {
        console.error('Erreur lors du partage:', error);
        return false;
      }
    } else {
      // Fallback pour les navigateurs qui ne supportent pas l'API Web Share
      try {
        await navigator.clipboard.writeText(shareData.url || window.location.origin);
        return true;
      } catch (error) {
        console.error('Erreur lors de la copie:', error);
        return false;
      }
    }
  };

  return {
    ...state,
    installApp,
    updateApp,
    shareApp,
    canInstall: state.isInstallable && !state.isInstalled,
  };
};

